import { useCallback, useEffect, useRef, useState } from "react";
import { api, handleSessionExpired, streamQuery, UnauthorizedError } from "@/api/client";
import type { Attachment, ChatMessage, HistoryTurn, QueryDone } from "@/api/types";
import { toast } from "@/lib/toast";
import { notify } from "@/lib/notify";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function newThreadId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

const THREAD_KEY = "arkiveai.chat.thread";

// 스킬 칩 hint → 사용자 말풍선 표시 라벨 (Composer SKILL_CHIPS 와 동기화)
const SKILL_HINT_LABELS: Record<string, string> = {
  meeting: "회의록 작성(요약식)",
  meeting_transcript: "회의록 작성(녹취식)",
  summary: "요약서 작성",
  translate: "번역서 작성",
  analyze: "분석서 작성",
};

export function useChatStream() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [threadId, setThreadId] = useState<string>(
    () => localStorage.getItem(THREAD_KEY) || newThreadId(),
  );
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    localStorage.setItem(THREAD_KEY, threadId);
  }, [threadId]);

  const patch = useCallback((id: string, p: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...p } : m)));
  }, []);

  const applyDone = useCallback(
    (botId: string, d: QueryDone) => {
      patch(botId, {
        text: d.answer ?? "",
        streaming: false,
        progress: null,
        sources: d.sources ?? [],
        sourceMeta: d.source_meta ?? [],
        noAnswer: !!d.no_answer,
        escalation: d.escalation ?? null,
        sessionId: d.session_id ?? null,
        externalRequired: !!d.external_required,
        confirmToken: d.confirm_token ?? null,
        blocked: !!d.blocked,
      });
    },
    [patch],
  );

  // 401 수신 시 자동 SSO 리다이렉트 금지(정책: SSO 는 로그인 버튼에서만 시작).
  // 중앙 처리(handleSessionExpired)가 /me 재검증 후 진짜 만료일 때만 로그인 화면으로
  // 전환한다. (과거: loginUrl 로 즉시 이동 → 유효한 pilgrims 쿠키와 겹치면 SSO 를
  // 왕복하고 돌아와 대화 중 화면이 새로고침된 것처럼 보이던 문제)
  const reauth = () => {
    toast("로그인이 필요합니다.", "error");
    handleSessionExpired();
  };

  // 답변 완료 후 후속 추천 질문 비동기 로드(실패 무시)
  const fetchSuggest = useCallback(
    (botId: string, question: string, answer: string) => {
      if (!question.trim() || !answer.trim()) return;
      api
        .suggest(question, answer)
        .then((r) => {
          if (r.suggestions?.length) patch(botId, { suggestions: r.suggestions });
        })
        .catch(() => {});
    },
    [patch],
  );

  const send = useCallback(
    async (question: string, customer: string, modelId?: number | null) => {
      if (busy || !question.trim()) return;
      setBusy(true);
      const botId = uid();
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: question },
        { id: botId, role: "bot", text: "", streaming: true },
      ]);

      const ac = new AbortController();
      abortRef.current = ac;
      let acc = "";
      let doneSeen = false;

      await streamQuery(
        question,
        customer,
        threadId,
        {
          onChunk: (t) => {
            acc += t;
            // 첫 토큰이 흐르기 시작하면 진행률 표시는 끈다(본문으로 전환).
            patch(botId, { text: acc, streaming: true, progress: null });
          },
          onProgress: (p) => {
            // 보고서 종합 등 장시간 작업: 본문 토큰이 아직 없을 때 진행률을 보여준다.
            if (!acc) patch(botId, { progress: p, streaming: true });
          },
          onDone: (d) => {
            doneSeen = true;
            applyDone(botId, d);
            if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || acc);
          },
          onError: async (msg) => {
            if (msg === "unauthorized") return reauth();
            try {
              const d = await api.query(question, customer, threadId, modelId);
              doneSeen = true;
              applyDone(botId, d);
              if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || "");
            } catch (e) {
              if (e instanceof UnauthorizedError) return reauth();
              patch(botId, {
                text: "답변을 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.",
                streaming: false,
                error: true,
              });
            }
          },
        },
        ac.signal,
        modelId, // 사용자가 선택해 유지 중인 모델(미지정이면 서버 기본값 모델)
      );

      if (!doneSeen) patch(botId, { streaming: false });
      setBusy(false);
      abortRef.current = null;
    },
    [busy, threadId, patch, applyDone, fetchSuggest],
  );

  const confirmExternal = useCallback(
    async (botId: string, token: string, accept: boolean) => {
      patch(botId, { externalRequired: false, confirmToken: null });
      const replyId = uid();
      setMessages((prev) => [...prev, { id: replyId, role: "bot", text: "", streaming: true }]);
      try {
        const d = accept ? await api.confirm(token) : await api.decline(token);
        applyDone(replyId, d);
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(replyId, { text: "처리에 실패했습니다.", streaming: false, error: true });
      }
    },
    [patch, applyDone],
  );

  // 참고문서 일부를 제외(deselect)하고 동일 질문 재생성 (웹 전용·비스트리밍)
  const regenerateWithSources = useCallback(
    async (question: string, customer: string, excludeUrls: string[]) => {
      if (busy || !question.trim() || excludeUrls.length === 0) return;
      setBusy(true);
      const botId = uid();
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: `↻ 참고문서 ${excludeUrls.length}건 제외하고 다시 답변` },
        { id: botId, role: "bot", text: "", streaming: true },
      ]);
      try {
        const d = await api.regenerate(question, customer, threadId, excludeUrls);
        applyDone(botId, d);
        if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || "");
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(botId, { text: "재생성에 실패했습니다. 잠시 후 다시 시도해 주세요.", streaming: false, error: true });
      } finally {
        setBusy(false);
      }
    },
    [busy, threadId, patch, applyDone, fetchSuggest],
  );

  const setFeedback = useCallback(
    (id: string, fb: 1 | -1) => patch(id, { feedback: fb }),
    [patch],
  );

  const setBookmark = useCallback(
    (id: string, bookmarkId: number | null) => patch(id, { bookmarkId }),
    [patch],
  );

  // 첨부(다중) + 지시 → 스킬 실행(회의록·요약·비교 등). qa 판정 시 일반 질의로 폴백.
  const runSkill = useCallback(
    async (files: File[], customer: string, text: string, hint = "", modelId?: number | null) => {
      if (busy) return;
      setBusy(true);
      const botId = uid();
      // 첨부 목록은 말풍선 아래 파일 칩으로 따로 보여주므로 문구에 파일 개수를 넣지 않는다.
      // 지시문이 없으면 "무엇을 요청했는지"만 남기고, 파일명은 칩이 담당.
      const q =
        (text || "").trim() ||
        (hint ? `[${SKILL_HINT_LABELS[hint] ?? hint}] 요청` : "[파일 처리 요청]");
      const attachments: Attachment[] = files.map((f) => ({
        name: f.name,
        size: f.size,
        mime: f.type || null,
      }));
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: q, attachments },
        { id: botId, role: "bot", text: "", streaming: true },
      ]);
      try {
        const res = await api.skill(files, text, threadId, hint);
        if (res.status === "route_to_qa") {
          // 일반 Q&A → 첨부를 세션문서로 올린 뒤 스트리밍 질의
          // (text 에는 검색 방법 prefix([웹검색]/[하이브리드])가 포함될 수 있어
          //  웹/하이브리드 검색에도 첨부가 참고자료로 함께 쓰인다)
          if (!text.trim()) {
            patch(botId, {
              text: "첨부 파일로 무엇을 할지 지시를 입력해 주세요. (예: 요약·번역 버튼 클릭 또는 자유 질문)",
              streaming: false,
            });
            return;
          }
          for (const f of files) {
            try {
              await api.upload(f, threadId);
            } catch {
              /* 업로드 실패해도 질의는 진행 */
            }
          }
          let acc = "";
          let done = false;
          await streamQuery(
            text,
            customer,
            threadId,
            {
              onChunk: (t) => {
                acc += t;
                patch(botId, { text: acc, streaming: true });
              },
              onDone: (d) => {
                done = true;
                applyDone(botId, d);
              },
              onError: async () => {
                try {
                  const d = await api.query(text, customer, threadId, modelId);
                  done = true;
                  applyDone(botId, d);
                } catch {
                  patch(botId, { text: "답변을 가져오지 못했습니다.", streaming: false, error: true });
                }
              },
            },
            undefined,
            modelId, // 첨부 QA 폴백에도 동일하게 선택 모델 적용
          );
          if (!done) patch(botId, { streaming: false });
        } else if (res.success) {
          patch(botId, {
            text: res.answer || `${res.label ?? "처리"} 완료`,
            streaming: false,
            downloads: res.downloads ?? [],
            sessionId: res.query_id ?? null,
          });
          // 처리에 시간이 걸려 사용자가 다른 탭을 보고 있으면 완료 알림
          if (document.hidden) {
            notify(`${res.label ?? "작업"} 완료`, (res.downloads?.length ?? 0) > 0 ? "다운로드가 준비되었습니다." : undefined);
          }
        } else {
          patch(botId, {
            text: `${res.label ?? "처리"} 실패: ${res.error || res.answer || "오류"}`,
            streaming: false,
            error: true,
          });
        }
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(botId, { text: "처리 중 오류가 발생했습니다.", streaming: false, error: true });
      } finally {
        setBusy(false);
      }
    },
    [busy, threadId, patch, applyDone],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    setBusy(false);
  }, []);

  // 새 대화 시작 — 새 thread + 화면 비움
  const newThread = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(newThreadId());
    setMessages([]);
    setBusy(false);
  }, []);

  // 이력에서 특정 대화 불러오기
  const loadThread = useCallback((id: string, turns: HistoryTurn[]) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(id);
    const msgs: ChatMessage[] = [];
    for (const t of turns) {
      const attachments = t.attachments ?? [];
      // 질문 문구가 비어도 첨부가 있으면 질문 말풍선을 만들어 "무슨 파일을 보낸 요청"인지 보여준다.
      if (t.question || attachments.length > 0)
        msgs.push({
          id: uid(),
          role: "user",
          text: t.question || "[파일 처리 요청]",
          attachments,
        });
      msgs.push({
        id: uid(),
        role: "bot",
        text: t.answer || "",
        streaming: false,
        sessionId: t.query_id || null,
        downloads: t.downloads ?? [], // 스킬 산출물 다운로드 버튼 복원
      });
    }
    setMessages(msgs);
    setBusy(false);
  }, []);

  return {
    messages,
    busy,
    threadId,
    send,
    runSkill,
    stop,
    newThread,
    loadThread,
    confirmExternal,
    setFeedback,
    setBookmark,
    regenerateWithSources,
    reset: newThread,
  };
}
